/**
 * KRILL ISSUES — composition root. Stage 4: the shell.
 *
 * Boot the renderer, hand the canvas to the App, and open a screen. Everything
 * else lives in `src/game/` (screens, run) or below it (sim, meta, save) —
 * this file only decides where a session starts (doc 12 §Code health).
 */

import { audio } from './audio/engine.ts';
import { assertContent } from './content/validate.ts';
import { App, createApp } from './game/app.ts';
import { bootDone, bootProgress, crashScreen, installCrashScreen } from './game/boot.ts';
import { RunScreen } from './game/run.ts';
import { DepthScreen } from './game/screens/depth.ts';
import { ResultsScreen } from './game/screens/results.ts';
import { SlotScreen } from './game/screens/slots.ts';
import { TitleScreen } from './game/screens/title.ts';
import { scenarioFor, skeletonScenario } from './sim/scenario.ts';
import type { Sim } from './sim/sim.ts';

const params = new URLSearchParams(location.search);
/** `?seed=n` overrides — replay debugging today, daily seeds one day (doc 12). */
const RUN_SEED = Number(params.get('seed')) || 20260824;
/** `?depth=2` opens the harness run on a deeper rung — art and audio checks. */
const RUN_DEPTH = Math.max(1, Math.min(3, Number(params.get('depth')) || 1));

/** Set once the shell exists, so the crash screen can still hand back a save. */
let live: App | null = null;

async function boot(): Promise<void> {
  // doc 18 §6: installed FIRST, so a crash during boot still has a screen
  installCrashScreen(() => (live?.slot ? live.store.exportSlot(live.slot) : null));
  bootProgress('boot');
  // doc 18 §8: a content edit that breaks the game says so on frame one, not in
  // fight nine. Loud in dev; logged in a build, because a black screen over a
  // typo is worse than a wrong number.
  assertContent(import.meta.env.DEV);

  const canvas = document.getElementById('stage') as HTMLCanvasElement | null;
  if (!canvas) throw new Error('no #stage canvas');

  bootProgress('renderer');
  const app = await createApp(canvas);
  live = app;
  bootProgress('atlases');
  bootProgress('save');

  // `?run=1` skips the shell and starts a profile-free D1 run: the golden
  // replays are verified against a run with NEUTRAL modifiers, and this is how
  // a browser session reproduces the headless harness exactly (doc 12 §T4).
  if (params.has('run')) {
    app.go(
      (a) =>
        new RunScreen(a, {
          scenario: RUN_DEPTH === 1 ? skeletonScenario() : scenarioFor(RUN_DEPTH),
          seed: RUN_SEED,
          onExit: (result) => {
            if (result) a.go((b) => new ResultsScreen(b, result));
            else a.go((b) => new TitleScreen(b));
          },
        }),
    );
  } else {
    const slot = app.store.lastSlot();
    const loaded = slot ? app.store.loadSlot(slot) : null;
    if (slot && loaded?.profile) {
      app.useProfile(slot, loaded.profile);
      app.go((a) => new TitleScreen(a));
    } else {
      // no colony yet: the slot picker IS the first-run experience
      app.go((a) => new SlotScreen(a));
    }
  }

  bootProgress('ready');
  bootDone();
  installDevHook(app);
}

/**
 * Dev/test hook: read-only state access plus coordinate helpers, so scripted
 * browser tests drive the game through REAL mouse and keyboard events. No
 * command bypass lives here on purpose — input goes through the same path as a
 * hand does.
 */
function installDevHook(app: App): void {
  let pumpClock = 0;
  Object.defineProperty(window, '__KI', {
    value: {
      get app(): App {
        return app;
      },
      get audio(): typeof audio {
        return audio;
      },
      get sim(): Sim | null {
        const screen = app.current;
        return screen instanceof RunScreen ? screen.sim : null;
      },
      /** Jump straight into a neutral D1 run (no profile, no meta). */
      startRun(seed = RUN_SEED): void {
        app.slot = null;
        app.profile = null;
        app.go(
          (a) =>
            new RunScreen(a, {
              scenario: skeletonScenario(),
              seed,
              onExit: (result) => {
                if (result) a.go((b) => new ResultsScreen(b, result));
                else a.go((b) => new TitleScreen(b));
              },
            }),
        );
      },
      go(screen: 'title' | 'slots' | 'depth'): void {
        if (screen === 'title') app.go((a) => new TitleScreen(a));
        else if (screen === 'slots') app.go((a) => new SlotScreen(a));
        else app.go((a) => new DepthScreen(a));
      },
      pump(frames = 1): void {
        if (pumpClock === 0) pumpClock = performance.now();
        for (let k = 0; k < frames; k++) {
          pumpClock += 1000 / 60;
          app.layers.app.ticker.update(pumpClock);
        }
      },
      tileClient(i: number, j: number): { x: number; y: number } | null {
        const screen = app.current;
        return screen instanceof RunScreen ? screen.tileClient(i, j) : null;
      },
      shoalClient(id: string): { x: number; y: number } | null {
        const screen = app.current;
        return screen instanceof RunScreen ? screen.shoalClient(id) : null;
      },
    },
  });
}

boot().catch((err: unknown) => {
  // Doc 18 §6: a crash never leaves a blank canvas with no explanation, and a
  // boot failure is just a crash with a clearer cause.
  document.getElementById('boot')?.classList.add('failed');
  const label = document.getElementById('boot-label');
  if (label) label.textContent = 'THE HATCH DID NOT OPEN.';
  document.body.append(
    crashScreen(
      `KRILL ISSUES — BOOT FAILED\n${String(err instanceof Error ? (err.stack ?? err.message) : err)}`,
      () => (live?.slot ? live.store.exportSlot(live.slot) : null),
    ),
  );
  console.error(err);
});
